/**
 * RouteMap — Phase 8 Library Edition continuation.
 *
 * Stylized period-inspired route-map inset that communicates "you are here"
 * during the streetcar ride from Birmingham to Edgewood Lake via Lone Pine Gap.
 *
 * Not a real geographic map — an illustrated 1920s-era streetcar system-map
 * style route diagram with three labeled anchors:
 *
 *   BIRMINGHAM ─── LONE PINE GAP ─── EDGEWOOD LAKE
 *
 * A moving streetcar dot tracks `useStreetcarStore.progress` (0..1) along
 * the route. Visible while `navState === 'streetcarRiding'` AND during the
 * arrival cinematic (so the dot completes its journey to the lake). Also
 * visible during `boarding` at t=0 so the viewer sees the full route
 * before departure.
 *
 * Implementation notes:
 *   - Pure React SVG DOM overlay; NO R3F hooks, NO map library, NO tile
 *     service, NO external fetches. Deterministic render from the single
 *     progress scalar + navState.
 *   - Parented in UIShell at z-index 35 (above BoundaryFeedback=10, below
 *     ReturnToStreetcarButton=40, in the same band as Minimap=30 — but
 *     the two are mutually exclusive by navState so they never collide).
 *   - Top-right corner (Minimap is bottom-right); both never visible at
 *     the same time but positioning them on different edges keeps the
 *     period-quiet chrome readable on overlap-edge cases.
 *   - Period-quiet chrome: cream background with espresso text + warm-ochre
 *     accents + Goudy Old Style serif (AESTHETIC-BRIEF Rule 7). Matches
 *     the title-card + location-label typography family.
 *
 * The 3-anchor SVG layout deliberately simplifies the real ~5mi streetcar
 * route into a horizontal diagram. Attempting a geographically accurate
 * minimap for a 5-mile route would be wasted effort and would fight the
 * period aesthetic. The 1925 streetcar system maps this references were
 * themselves illustrative, not survey-accurate.
 */
import { useNavStore } from '../store/useNavStore';
import { useStreetcarStore } from '../store/useStreetcarStore';
import { typography, periodChrome } from './PeriodChromeStyles';

// ---- Chrome ----
const WIDTH = 280;
const HEIGHT = 110;
const PAD_X = 22;

// Route-line endpoints in SVG local coords. BIRMINGHAM on the left, LAKE on right.
// Lone Pine Gap at the visual midpoint mirrors the geographic reality — the
// real route crosses Red Mountain through Lone Pine Gap roughly halfway
// between Five Points South and the Edgewood terminus.
const BIRMINGHAM_X = PAD_X;
const LAKE_X = WIDTH - PAD_X;
const LINE_Y = 68;
const GAP_X = (BIRMINGHAM_X + LAKE_X) / 2;

// Render "hill" marker at Lone Pine Gap — a stylized triangle above the
// route line evokes the Red Mountain cut the streetcar passed through.
const HILL_HALF_W = 14;
const HILL_H = 12;

export function RouteMap() {
  const navState = useNavStore((s) => s.navState);
  const progress = useStreetcarStore((s) => s.progress);

  // Visibility gate. Show during the full streetcar journey narrative:
  //   - `boarding`          → dot parked at Birmingham (t=0), preview full route
  //   - `streetcarRiding`   → dot tracks progress along the line
  //   - `arrivalCinematic`  → dot pinned at the lake end (the ride is done
  //                           but we want the "arrived" state visible for
  //                           the cinematic)
  // Hidden during idle (pre-ride) and all post-arrival states (freeWalk,
  // guidedTour, photoCompare) so the inset doesn't compete with Minimap.
  const visible =
    navState === 'boarding' ||
    navState === 'streetcarRiding' ||
    navState === 'arrivalCinematic';
  if (!visible) return null;

  // Dot position: clamp to [0, 1] and remap onto the BIRMINGHAM→LAKE line.
  // During `boarding` progress is 0 (dot at Birmingham). During
  // `arrivalCinematic` we pin at 1 regardless of residual store state —
  // the ride is over, the dot belongs at the lake.
  let t = Math.max(0, Math.min(1, progress));
  if (navState === 'boarding') t = 0;
  if (navState === 'arrivalCinematic') t = 1;
  const dotX = BIRMINGHAM_X + (LAKE_X - BIRMINGHAM_X) * t;

  return (
    <div
      role="img"
      aria-label="Route map: streetcar position from Birmingham to Edgewood Lake"
      style={{
        position: 'fixed',
        top: '16px',
        right: '16px',
        width: `${WIDTH}px`,
        height: `${HEIGHT}px`,
        zIndex: 35,
        pointerEvents: 'none',
      }}
      data-testid="route-map"
    >
      <svg
        width={WIDTH}
        height={HEIGHT}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Cream paper background with a warm-ochre border. Matches the
            period-quiet chrome the location-label + title-cards use. */}
        <rect
          data-background
          x={1}
          y={1}
          width={WIDTH - 2}
          height={HEIGHT - 2}
          fill={periodChrome.cream}
          stroke={periodChrome.ochre}
          strokeWidth={1.2}
          rx={3}
        />

        {/* Title band — caps serif across the top. Anchors the map as
            "Birmingham & Edgewood Electric Railway" per §3.1 of the
            historical research. Small text so the map reads as a caption,
            not a billboard. */}
        <text
          x={WIDTH / 2}
          y={18}
          textAnchor="middle"
          fontFamily={typography.serif}
          fontSize={9}
          fontStyle="italic"
          fill={periodChrome.espresso}
          letterSpacing="0.12em"
        >
          BIRMINGHAM &amp; EDGEWOOD ELECTRIC RAILWAY
        </text>
        <text
          x={WIDTH / 2}
          y={30}
          textAnchor="middle"
          fontFamily={typography.serif}
          fontSize={8}
          fill={periodChrome.espresso}
          opacity={0.65}
          letterSpacing="0.08em"
        >
          No. 39 — 5.4 miles
        </text>

        {/* Route line — warm-ochre solid bar under the anchor labels.
            Double-line styling evokes a period rail diagram rather than
            a single stroke (streetcar tracks = two rails). */}
        <line
          x1={BIRMINGHAM_X}
          y1={LINE_Y}
          x2={LAKE_X}
          y2={LINE_Y}
          stroke={periodChrome.ochre}
          strokeWidth={2.5}
          strokeLinecap="round"
        />
        <line
          x1={BIRMINGHAM_X}
          y1={LINE_Y + 3}
          x2={LAKE_X}
          y2={LINE_Y + 3}
          stroke={periodChrome.ochre}
          strokeWidth={1}
          strokeLinecap="round"
          opacity={0.55}
        />

        {/* Lone Pine Gap hill silhouette — stylized triangle above the mid
            line evokes Red Mountain + the 72-foot-deep cut the No. 39
            threaded through. Muted green fill ties to the AESTHETIC-BRIEF
            palette. The line passes THROUGH the hill via the cut. */}
        <polygon
          data-landmark="lone-pine-gap"
          points={`${GAP_X - HILL_HALF_W},${LINE_Y} ${GAP_X},${LINE_Y - HILL_H} ${GAP_X + HILL_HALF_W},${LINE_Y}`}
          fill={periodChrome.mutedGreen}
          opacity={0.35}
          stroke={periodChrome.espresso}
          strokeWidth={0.6}
        />

        {/* Anchor 1 — BIRMINGHAM (left). Small ochre dot + serif label. */}
        <circle
          data-anchor="birmingham"
          cx={BIRMINGHAM_X}
          cy={LINE_Y}
          r={3.2}
          fill={periodChrome.espresso}
        />
        <text
          x={BIRMINGHAM_X}
          y={LINE_Y + 18}
          textAnchor="start"
          fontFamily={typography.serif}
          fontSize={9}
          fill={periodChrome.espresso}
          letterSpacing="0.06em"
        >
          BIRMINGHAM
        </text>

        {/* Anchor 2 — LONE PINE GAP (middle). Dot sits at the hill apex. */}
        <circle
          data-anchor="lone-pine-gap"
          cx={GAP_X}
          cy={LINE_Y - HILL_H}
          r={2.2}
          fill={periodChrome.warmOchre}
        />
        <text
          x={GAP_X}
          y={LINE_Y - HILL_H - 4}
          textAnchor="middle"
          fontFamily={typography.serif}
          fontSize={8}
          fontStyle="italic"
          fill={periodChrome.espresso}
          letterSpacing="0.06em"
        >
          Lone Pine Gap
        </text>

        {/* Anchor 3 — EDGEWOOD LAKE (right). */}
        <circle
          data-anchor="edgewood-lake"
          cx={LAKE_X}
          cy={LINE_Y}
          r={3.2}
          fill={periodChrome.espresso}
        />
        <text
          x={LAKE_X}
          y={LINE_Y + 18}
          textAnchor="end"
          fontFamily={typography.serif}
          fontSize={9}
          fill={periodChrome.espresso}
          letterSpacing="0.06em"
        >
          EDGEWOOD LAKE
        </text>

        {/* Moving streetcar position marker — warm-ochre filled circle with
            an espresso ring. Larger than the anchor dots so the viewer's
            eye catches it immediately. The tiny vertical stroke above
            evokes a trolley pole silhouette, reinforcing "that's the
            streetcar, that's you." */}
        <g data-streetcar transform={`translate(${dotX.toFixed(2)} ${LINE_Y})`}>
          <line
            x1={0}
            y1={-9}
            x2={0}
            y2={-3}
            stroke={periodChrome.espresso}
            strokeWidth={0.8}
          />
          <circle r={4.8} fill={periodChrome.warmOchre} stroke={periodChrome.espresso} strokeWidth={1} />
          <circle r={1.6} fill={periodChrome.cream} />
        </g>
      </svg>
    </div>
  );
}
